import { useState } from "react";
import { PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ContactForm({ defaultName = "", defaultEmail = "", compact = false }) {
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@") || message.trim().length < 5) {
      toast.error("Please add a valid email and a short message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { name, email, subject, message });
      setSent(true);
      setSubject(""); setMessage("");
      toast.success("Message sent — we'll be in touch!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-success" className="rounded-2xl border border-riq-border bg-white p-8 text-center">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-riq-sage/60 text-riq-primary">
          <CheckCircle size={26} weight="duotone" />
        </div>
        <h3 className="font-head text-lg font-bold text-riq-primary">Thanks — message sent!</h3>
        <p className="mt-1 text-sm text-muted-foreground">Our team will get back to you at {email} soon.</p>
        <button data-testid="contact-send-another" onClick={() => setSent(false)} className="mt-4 text-sm font-semibold text-riq-primary hover:underline">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-3">
      <div className={`grid gap-3 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Name</label>
          <input data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
            className="mt-1 w-full rounded-xl border border-riq-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-riq-primary" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Email</label>
          <input data-testid="contact-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
            className="mt-1 w-full rounded-xl border border-riq-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-riq-primary" />
        </div>
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Subject</label>
        <input data-testid="contact-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What's this about?"
          className="mt-1 w-full rounded-xl border border-riq-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-riq-primary" />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Message</label>
        <textarea data-testid="contact-message" required rows={compact ? 4 : 5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="How can we help?"
          className="mt-1 w-full resize-none rounded-xl border border-riq-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-riq-primary" />
      </div>
      <button data-testid="contact-submit" type="submit" disabled={sending}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-riq-primary py-3 text-sm font-semibold text-white hover:-translate-y-0.5 active:scale-95 transition-transform disabled:opacity-50">
        <PaperPlaneTilt size={16} weight="bold" /> {sending ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
